import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize, Observable, Subscription, tap, throwError } from 'rxjs';

import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { PERRON_POINTS_COPY } from '../perron-points/perron-points-copy';
import { PP_THRESHOLD_FALLBACK } from '../perron-points/perron-points.constants';
import {
  PerronPointsError,
  PerronPointsErrorReason,
  PerronPointsRedeemResponse,
  PerronPointsSummary,
} from '../models/perron-points.model';

/**
 * Fuente única del estado de Perrón Points en el front. Carga el saldo
 * automáticamente en cuanto hay sesión (efecto sobre `AuthService.isAuthenticated`)
 * y lo limpia al hacer logout — ningún componente necesita "disparar" la carga
 * manualmente, basta con inyectar el servicio (patrón análogo a
 * `StockAlertModalService`).
 */
@Injectable({ providedIn: 'root' })
export class PointsService {
  private readonly api = inject(ApiService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);

  private readonly _summary = signal<PerronPointsSummary | null>(null);
  private readonly _loading = signal(false);
  private readonly _loadError = signal(false);
  /**
   * Último umbral EA conocido, de CUALQUIER fuente (un `/perron-points/me`
   * resuelto o un 403 EA mapeado en `mapEarlyAccessGateError`). Sobrevive a
   * que `_summary` se limpie (p. ej. al hacer logout) — sigue siendo el mejor
   * valor conocido hasta que llegue uno nuevo.
   */
  private readonly _lastKnownThreshold = signal<number | null>(null);
  private refreshSubscription: Subscription | null = null;

  readonly summary = this._summary.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly loadError = this._loadError.asReadonly();
  readonly balance = computed(() => this._summary()?.balance ?? 0);
  readonly earlyAccess = computed(() => this._summary()?.early_access ?? false);
  readonly threshold = computed<number | null>(() => this._summary()?.threshold ?? null);

  /**
   * Umbral EA para TEXTOS DE CARA AL CLIENTE (nunca `null`): el último
   * conocido del servidor, o `PP_THRESHOLD_FALLBACK` mientras no se conozca
   * ninguno todavía (p. ej. invitado que nunca llamó `/perron-points/me`).
   * No usar para gating — el gate real usa `threshold()` (nullable: `null`
   * significa "aún no sabemos", y el gate se queda cerrado ese tiempo).
   */
  readonly displayThreshold = computed<number>(
    () => this._lastKnownThreshold() ?? PP_THRESHOLD_FALLBACK,
  );

  /** PP que faltan para el umbral de Early Access, o `null` si aún no hay summary cargado. */
  readonly missingForThreshold = computed<number | null>(() => {
    const threshold = this.threshold();
    if (threshold === null) return null;
    return Math.max(0, threshold - this.balance());
  });

  constructor() {
    effect(() => {
      if (this.authService.isAuthenticated()) {
        this.refresh();
      } else {
        this.refreshSubscription?.unsubscribe();
        this.refreshSubscription = null;
        this._summary.set(null);
        this._loading.set(false);
        this._loadError.set(false);
      }
    });
  }

  me(): Observable<PerronPointsSummary> {
    return this.api.get<PerronPointsSummary>('/perron-points/me').pipe(
      tap((summary) => {
        this._summary.set(summary);
        this._lastKnownThreshold.set(summary.threshold);
      }),
    );
  }

  /** Persiste un umbral EA visto fuera de `/perron-points/me` (p. ej. un 403 EA). */
  recordThreshold(threshold: number): void {
    this._lastKnownThreshold.set(threshold);
  }

  /** Recarga el saldo en segundo plano (fire-and-forget). */
  refresh(): void {
    this.refreshSubscription?.unsubscribe();
    this._loading.set(true);
    this._loadError.set(false);

    this.refreshSubscription = this.me()
      .pipe(finalize(() => this._loading.set(false)))
      .subscribe({
        error: () => {
          this._summary.set(null);
          this._loadError.set(true);
          this.toastService.show({
            type: 'error',
            message: PERRON_POINTS_COPY.common.loadError,
          });
        },
      });
  }

  /** Normaliza separadores (`-`/espacios) antes de canjear, como hace el backend. */
  redeem(code: string): Observable<PerronPointsRedeemResponse> {
    const normalized = code.replace(/[-\s]/g, '');
    return this.api
      .post<PerronPointsRedeemResponse>('/perron-points/redeem', { code: normalized })
      .pipe(
        tap(() => this.refresh()),
        catchError((err: unknown) => this.mapRedeemError(err)),
      );
  }

  private mapRedeemError(err: unknown): Observable<never> {
    if (!(err instanceof HttpErrorResponse)) return throwError(() => err);

    let reason: PerronPointsErrorReason = 'unknown';
    if (err.status === 422) reason = 'invalid';
    else if (err.status === 409) reason = 'consumed';
    else if (err.status === 429) reason = 'throttled';

    return throwError(
      () =>
        new PerronPointsError(reason, err.error?.error ?? PERRON_POINTS_COPY.redeemModal.error),
    );
  }
}
